// Decides whether saving the recipe form should CREATE (POST /recipes) or
// UPDATE (PATCH /recipes/:id), and builds/validates the request payload.
//
// Regression context (GitHub issue #74): the admin "New Recipe" button routes
// to /recipes/new, which the router matches as /recipes/:id with id === 'new'.
// Treating 'new' as a recipe id sent PATCH /api/v1/recipes/new, which the API
// rejects with 400 (id is not a UUID). The 'new' sentinel must instead create
// a recipe via POST, never issue a PATCH/GET against the literal id 'new'.

import type { RecipeUnit } from '@/services/recipe.service';

export const NEW_RECIPE_ID = 'new';

export interface RecipeSaveInput {
  id: string | undefined;
  name: string;
  linkedProductId: string;
  yieldQty: string;
  instructions: string;
  ingredients: { product_id: string; quantity: number; unit: RecipeUnit; waste_percentage: number }[];
}

interface RecipeIngredientPayload {
  product_id: string;
  quantity: number;
  unit: RecipeUnit;
  waste_percentage: number;
}

export type RecipeSavePlan =
  | { mode: 'invalid'; error: string }
  | {
      mode: 'create';
      payload: {
        product_id: string;
        name: string;
        yield_quantity: number;
        instructions?: string;
        ingredients: RecipeIngredientPayload[];
      };
    }
  | {
      mode: 'update';
      id: string;
      payload: {
        name: string;
        yield_quantity: number;
        instructions?: string;
        ingredients: RecipeIngredientPayload[];
      };
    };

export function isNewRecipeId(id: string | undefined): boolean {
  return id === NEW_RECIPE_ID;
}

export function buildRecipeSavePlan(input: RecipeSaveInput): RecipeSavePlan {
  const name = input.name.trim();
  const instructions = input.instructions.trim() || undefined;
  const yieldQuantity = Number(input.yieldQty) || 1;
  const ingredients = input.ingredients.map((i) => ({
    product_id: i.product_id,
    quantity: i.quantity,
    unit: i.unit,
    waste_percentage: i.waste_percentage,
  }));

  if (isNewRecipeId(input.id)) {
    if (!name) return { mode: 'invalid', error: 'Recipe name is required.' };
    if (!input.linkedProductId) return { mode: 'invalid', error: 'Select a linked menu item.' };
    if (ingredients.length === 0) return { mode: 'invalid', error: 'Add at least one ingredient.' };
    return {
      mode: 'create',
      payload: { product_id: input.linkedProductId, name, yield_quantity: yieldQuantity, instructions, ingredients },
    };
  }

  return {
    mode: 'update',
    id: input.id as string,
    payload: { name, yield_quantity: yieldQuantity, instructions, ingredients },
  };
}
